import { captureException } from '@/utils/sentry';
import { createPerformanceSpanManager } from './span-manager';
import { Fennel } from '@/analytics/fennel/fennel';
import { getNetworkDetail } from '@/contexts/network-status-context';
import {
  performanceTracingRuntimeConfig,
  shouldSamplePerformanceTrace,
} from './runtime-config';
import { useExperienceStore } from '@/stores/experience-store';
import type {
  PerformanceEventCustomPayloads,
  PerformanceEventName,
  StartedSpan,
  TrackPerformanceEvent,
} from './types';

export const defaultTrackPerformanceEvent: TrackPerformanceEvent = (
  eventName,
  payload,
) => {
  const { emitStartEvents } = performanceTracingRuntimeConfig.get();
  const initialized = Fennel.getInstance().getInitializationState();
  // We still time sampled-in traces locally when this flag is off. This only
  // suppresses forwarding the start lifecycle event to the analytics engine.
  const shouldSuppressStartEvent =
    !emitStartEvents && payload.phase === 'start';

  if (!initialized || shouldSuppressStartEvent) {
    return;
  }

  initialized.engine.track(eventName, {
    ...initialized.state,
    ...getNetworkDetail(),
    appExperience: useExperienceStore.getState().mode,
    ...payload,
  });
};

export const performanceSpanManager = createPerformanceSpanManager({
  onError: captureException,
  trackPerformanceEvent: defaultTrackPerformanceEvent,
});

interface DefaultSpanPayloadOptions<E extends PerformanceEventName> {
  payload?: Partial<PerformanceEventCustomPayloads[E]>;
}

interface DefaultStartSpanOptions<E extends PerformanceEventName>
  extends DefaultSpanPayloadOptions<E> {
  parentSpanId?: string;
  traceId?: string;
}

export const findActiveSpan = <E extends PerformanceEventName>(
  spanName: E,
): StartedSpan<E> | undefined => {
  return performanceSpanManager.findActiveSpan(spanName);
};

export const startSpan = <E extends PerformanceEventName>(
  spanName: E,
  options?: DefaultStartSpanOptions<E>,
): StartedSpan<E> | undefined => {
  // Child spans inherit the trace decision from an already-started parent
  // instead of resampling against the current runtime config.
  if (options?.parentSpanId) {
    return performanceSpanManager.startSpan(spanName, options);
  }

  const { enabled, sampleRate } = performanceTracingRuntimeConfig.get();

  // Keep sampling and nullable no-op behavior in the app-facing wrappers so
  // instrumentation can call start/end/cancel directly while the core manager
  // remains a generic tracing primitive.
  if (!enabled || !shouldSamplePerformanceTrace(sampleRate)) {
    return undefined;
  }

  return performanceSpanManager.startSpan(spanName, options);
};

export const endSpan = <E extends PerformanceEventName>(
  span: StartedSpan<E> | null | undefined,
  options?: DefaultSpanPayloadOptions<E>,
) => {
  if (!span) {
    return;
  }

  performanceSpanManager.endSpan(span, options);
};

export const cancelSpan = <E extends PerformanceEventName>(
  span: StartedSpan<E> | null | undefined,
  options?: DefaultSpanPayloadOptions<E>,
) => {
  if (!span) {
    return;
  }

  performanceSpanManager.cancelSpan(span, options);
};

if (__DEV__) {
  // @ts-ignore available in Metro (native) and Webpack (web) HMR
  module.hot?.dispose(() => {
    performanceSpanManager.destroy();
  });
}
